// hooks/useZeno.js
import { useState, useCallback, useRef } from 'react';
import ZenoService from '../services/zeno.service';
import { useAuth } from '../useAuth';
import { useOrganizations } from './useOrganizations';

/**
 * Hook para interactuar con Zeno Bot
 */
export const useZeno = () => {
  const { currentAccount } = useOrganizations();
  const accountId = currentAccount?.id;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [zenoInfo, setZenoInfo] = useState(null);
  const [currentChatHistoryId, setCurrentChatHistoryId] = useState(null);
  const [chatHistories, setChatHistories] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const loadZenoChatsRef = useRef(); // Ref para evitar dependencias infinitas

  /**
   * Cargar información de Zeno
   */
  const loadZenoInfo = useCallback(async () => {
    try {
      const result = await ZenoService.getZenoInfo();
      if (result.success) {
        setZenoInfo(result.data);
      }
    } catch (err) {
      console.error('Error cargando info de Zeno:', err);
    }
  }, []);

  /**
   * Chatear con Zeno - actualizado para nueva API
   */
  const chatWithZeno = useCallback(async (message, workflowContext = null, chatHistoryId = null) => {
    if (!accountId || !message.trim()) return;

    setSending(true);
    setError(null);

    // Agregar mensaje del usuario inmediatamente
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      message: message,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // Mostrar que Zeno está escribiendo casi inmediatamente
      setTimeout(() => setIsTyping(true), 50);
      
      // Crear contexto web por defecto
      const context = {
        platform: "web",
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      };

      const result = await ZenoService.chatWithZeno({
        accountId,
        message,
        chatHistoryId: chatHistoryId || currentChatHistoryId, // Usar chatHistoryId actual si no se proporciona uno
        context,
        workflowContext,
        useMemory: true,
      });

      if (result.success) {
        // Guardar el chatHistoryId para continuar la conversación
        if (result.data.chatHistoryId) {
          setCurrentChatHistoryId(result.data.chatHistoryId);
        }

        // Agregar respuesta de Zeno
        const zenoMessage = {
          id: result.data.messageId || `zeno-${Date.now()}`,
          role: 'assistant',
          message: result.data.response,
          workflowSuggestion: result.data.workflowSuggestion || null,
          chatHistoryId: result.data.chatHistoryId,
          nextActions: result.data.nextActions || null,
          metadata: result.data.metadata || null,
          createdAt: new Date().toISOString(),
        };

        setMessages((prev) => {
          const withoutTemp = prev.filter((m) => m.id !== userMessage.id);
          return [...withoutTemp, userMessage, zenoMessage];
        });

        // Refrescar historial de chats después de enviar mensaje
        // NOTA: El backend debe asignar assistantId=00000000-0000-0000-0000-0000000000e0 para que aparezcan aquí
        if (typeof loadZenoChatsRef.current === 'function') {
          await loadZenoChatsRef.current();
        }

        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error comunicándose con Zeno';
      setError(errorMsg);
      console.error(err);
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
      return { success: false, error: { message: errorMsg } };
    } finally {
      setSending(false);
      setIsTyping(false);
    }
  }, [accountId, currentChatHistoryId]);

  /**
   * Obtener sugerencia de workflow
   */
  const suggestWorkflow = useCallback(async (workflowDescription, context = null) => {
    if (!accountId || !workflowDescription.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await ZenoService.suggestWorkflow({
        accountId, // Cambiar de userId a accountId
        workflowDescription,
        context,
        accountId,
      });

      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error obteniendo sugerencia de workflow';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  /**
   * Limpiar chat
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
    setIsTyping(false);
    setCurrentChatHistoryId(null); // Limpiar también el chatHistoryId
  }, []);

  /**
   * Cargar lista de chats con Zeno
   */
  const loadZenoChats = useCallback(async () => {
    if (!accountId) return;

    setLoadingChats(true);
    try {
      const result = await ZenoService.listZenoChats(accountId);
      if (result.success) {
        setChatHistories(result.data || []);
      } else {
        console.error('Error loading Zeno chats:', result.error);
      }
    } catch (error) {
      console.error('Error loading Zeno chats:', error);
    } finally {
      setLoadingChats(false);
    }
  }, [accountId]);

  // Asignar ref para poder llamar desde chatWithZeno
  loadZenoChatsRef.current = loadZenoChats;

  /**
   * Cargar una conversación específica
   */
  const loadConversation = useCallback(async (chatHistoryId) => {
    if (!chatHistoryId) return;

    setLoading(true);
    setError(null);
    
    try {
      const result = await ZenoService.loadChatMessages(chatHistoryId);
      
      if (result.success) {
        setMessages(result.data.messages || []);
        setCurrentChatHistoryId(chatHistoryId);
        return { success: true };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMsg = 'Error loading conversation';
      setError(errorMsg);
      console.error('loadConversation error:', error);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [setMessages, setCurrentChatHistoryId, setLoading, setError]);

  /**
   * Iniciar nueva conversación
   */
  const startNewConversation = useCallback(() => {
    clearChat();
    // Refrescar historial de chats al iniciar nueva conversación
    if (typeof loadZenoChatsRef.current === 'function') {
      loadZenoChatsRef.current();
    }
  }, [clearChat]);

  return {
    messages,
    loading,
    sending,
    isTyping,
    error,
    zenoInfo,
    currentChatHistoryId, // Exponer el chatHistoryId actual
    chatHistories, // Lista de conversaciones anteriores
    loadingChats, // Estado de carga de chats
    chatWithZeno,
    sendMessage: chatWithZeno, // Alias para compatibilidad
    suggestWorkflow,
    clearChat,
    loadZenoInfo,
    loadZenoChats, // Cargar lista de chats
    loadConversation, // Cargar conversación específica
    startNewConversation, // Iniciar nueva conversación
  };
};

/**
 * Hook para gestionar memoria de Zeno
 */
export const useZenoMemory = () => {
  const { user } = useAuth();
  const userId = user?.user_id;

  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Cargar memoria de Zeno
   */
  const loadMemory = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await ZenoService.getZenoMemory(userId);
      if (result.success) {
        setMemory(result.data);
      } else {
        // Si no existe memoria, no es un error crítico
        if (result.error.status !== 404) {
          setError(result.error.message);
        }
      }
    } catch (err) {
      console.error('Error cargando memoria de Zeno:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Crear o actualizar memoria
   */
  const saveMemory = useCallback(async (memoryContent) => {
    if (!userId || !memoryContent.trim()) return;

    setLoading(true);
    setError(null);

    try {
      let result;
      
      if (memory) {
        // Actualizar memoria existente
        result = await ZenoService.updateZenoMemory(userId, memoryContent);
      } else {
        // Crear nueva memoria
        result = await ZenoService.createZenoMemory({
          userId,
          memoryContent,
          memoryCapacity: 2000,
        });
      }

      if (result.success) {
        setMemory(result.data);
        return { success: true };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error guardando memoria';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [userId, memory]);

  /**
   * Eliminar memoria
   */
  const deleteMemory = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await ZenoService.deleteZenoMemory(userId);
      if (result.success) {
        setMemory(null);
        return { success: true };
      } else {
        setError(result.error.message);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = 'Error eliminando memoria';
      setError(errorMsg);
      console.error(err);
      return { success: false, error: { message: errorMsg } };
    } finally {
      setLoading(false);
    }
  }, [userId]);

  return {
    memory,
    loading,
    error,
    loadMemory,
    saveMemory,
    deleteMemory,
  };
};
